import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  useTheme,
} from '@mui/material';
import ActionBar from '../components/ActionBar';
import AlertBar from '../components/AlertBar';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import LoadingSpinner from '../components/LoadingSpinner';
import AbstractBackground from '../components/AbstractBackground';
import actionBarConfig from '../config/actionBarConfig';
import { setShowTiles, setIsActiveChat, setSelectedTileId, clearSelectedTileId, clearPromptItems, hideToast } from '../reducers/uiReducer';
import AdminUserDetails from '../components/AdminUserDetails';
import Footer from '../components/Footer';
import useResponsive from '../hooks/useResponsive';
import { clearMessages } from '../reducers/messagesReducer';

const UserDetails = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loading);
  const toast = useSelector((state) => state.ui.toast);
  const selectedUser = useSelector((state) => state.selectedUser);
  const containerRef = useRef(null);
  const { isTabletScreen, isMobileScreen } = useResponsive();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleNewChat = () => {
    dispatch(clearMessages());
    dispatch(clearSelectedTileId());
    dispatch(setShowTiles(true));
    dispatch(setIsActiveChat(false));
    dispatch(clearPromptItems());
  };

  const memoizedBackground = useMemo(
    () => <AbstractBackground animate={loading} containerRef={containerRef} />, [loading]
  );

  const breadcrumbPath = [
    { label: 'Admin Dashboard', link: '/admin' },
    { label: 'Manage Users', link: '/admin/users' },
    { label: 'User Details' },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        //backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          height: '100vh',
          width: '100%',
          minWidth: 300,
          maxWidth: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {memoizedBackground}

        <Box
          sx={{
            //backgroundColor: theme.palette.background.paper,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <ActionBar config={actionBarConfig} onNewChat={handleNewChat} />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            display: 'flex',
            boxShadow: 1,
            background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(to top,rgba(18, 18, 18, 0.8),rgba(29, 29, 29, 0.6))'
                            : 'linear-gradient(to top, rgba(255,255,255,0.2), rgba(0,116,211,0.1))',
            position: 'relative',
            overflowY: 'auto',
          }}
        >
          <AlertBar
            open={toast.open}
            onClose={() => dispatch(hideToast())}
            severity={toast.severity}
            message={toast.message}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.palette.background.paper,
              padding: 2,
              margin: 2,
              borderRadius: 2,
              boxShadow: 1,
              alignSelf: 'flex-start',
              width: '100%',
            }}
          >
            <AdminBreadcrumb path={breadcrumbPath} />
            <AdminUserDetails data={selectedUser} />
          </Box>
        </Box>

        <Footer
          sx={{
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        />
      </Box>
    </Box>
  );
};

export default UserDetails;